// src/components/CreatePrompts.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useParams} from 'react-router-dom';

import axiosInstance from '../utils/axiosInstance';
import CreatableSelect from "react-select/creatable";
import {
    Box,
    Card,
    CardContent,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    Button,
    Alert,
    CircularProgress,
    Tooltip,
    Grid,
    useTheme
} from "@mui/material";


const Loader = () => (
    <Box
        sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            borderRadius: 'inherit'
        }}
    >
        <CircularProgress size={50} />
    </Box>
);


// Shared options for reusability
const commonCommunicationStyles = [
    { value: 'empathetic', label: 'Empathetic' },
    { value: 'informative', label: 'Informative' },
    { value: 'reassuring', label: 'Reassuring' },
    { value: 'professional', label: 'Professional' },
    { value: 'objective', label: 'Objective' },
    { value: 'formal', label: 'Formal' },
    { value: 'encouraging', label: 'Encouraging' },
    { value: 'patient', label: 'Patient' },
    { value: 'enthusiastic', label: 'Enthusiastic' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'concise', label: 'Concise' },
    { value: 'detailed', label: 'Detailed' },
];

const dynamicFieldConfigurations = {
    "Medical Professionals": {
        _default: [
            { id: 'userName', label: "Doctor's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Dr. Emily Carter", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 10 years", type: 'text', templateLabel: "**Years of Experience:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles,
            },
            { id: 'relationshipGoal', label: 'Relationship Goal', placeholder: 'e.g., Trusted confidant, Efficient assistant', type: 'text' },
            { 
                id: 'proactivityLevel', 
                label: 'Proactivity Level', 
                type: 'dropdown',
                options: [
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High (Anticipates needs)', label: 'High (Anticipates needs)' },
                ]
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'patientCenteredCare', label: 'Patient-centered care' },
                    { value: 'evidenceBasedPractice', label: 'Evidence-based practice' },
                    { value: 'confidentiality', label: 'Confidentiality' },
                    { value: 'holisticApproach', label: 'Holistic Approach' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Financial Professionals": {
        "Tax Accountant": [
            { id: 'userName', label: "Accountant's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Sarah Chen, CPA", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 5 years", type: 'text', templateLabel: "**Years of Experience:**" },
            { id: 'areasOfExpertise', label: "Areas of Expertise", placeholder: "Individual Tax Returns, Small Business Tax...", type: 'textarea', templateLabel: "**Areas of Expertise:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['professional', 'objective', 'informative', 'concise', 'detailed'].includes(s.value)),
            },
            { id: 'relationshipGoal', label: 'Relationship Goal', placeholder: 'e.g., Trusted advisor', type: 'text' },
            { 
                id: 'proactivityLevel', 
                label: 'Proactivity Level', 
                type: 'dropdown',
                options: [
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High (Anticipates needs)', label: 'High (Anticipates needs)' },
                ]
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'accuracy', label: 'Accuracy' },
                    { value: 'integrity', label: 'Integrity' },
                    { value: 'compliance', label: 'Compliance' },
                    { value: 'timeliness', label: 'Timeliness' },
                ],
                placeholder: "Add new key value"
            },
        ],
        _default: [
            { id: 'userName', label: "Professional's Name", placeholder: "e.g., John Doe", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 7 years", type: 'text', templateLabel: "**Years of Experience:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles,
            },
            { id: 'relationshipGoal', label: 'Relationship Goal', placeholder: 'e.g., Trusted advisor', type: 'text' },
            { 
                id: 'proactivityLevel', 
                label: 'Proactivity Level', 
                type: 'dropdown',
                options: [
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High (Anticipates needs)', label: 'High (Anticipates needs)' },
                ]
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'clientFirst', label: 'Client First' },
                    { value: 'transparency', label: 'Transparency' },
                    { value: 'ethicalPractices', label: 'Ethical Practices' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Legal Professionals": {
        _default: [
            { id: 'userName', label: "Lawyer's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., James O'Connell, Esq.", type: 'text', templateLabel: "**Name:**" },
            { id: 'areasOfExpertise', label: "Area of Expertise", placeholder: "Contract Law, Real Estate Law...", type: 'textarea', templateLabel: "**Area of Expertise:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 3 years", type: 'text', templateLabel: "**Years of Experience:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['formal', 'objective', 'informative', 'assertive', 'persuasive'].includes(s.value) || !commonCommunicationStyles.find(cs => cs.value === s.value)),
            },
            { id: 'relationshipGoal', label: 'Relationship Goal', placeholder: 'e.g., Diligent counsel', type: 'text' },
            { 
                id: 'proactivityLevel', 
                label: 'Proactivity Level', 
                type: 'dropdown',
                options: [
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High (Anticipates needs)', label: 'High (Anticipates needs)' },
                ]
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'upholdingTheLaw', label: 'Upholding the law' },
                    { value: 'clientConfidentiality', label: 'Client confidentiality' },
                    { value: 'justice', label: 'Justice' },
                    { value: 'dueDiligence', label: 'Due Diligence' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Educational Professionals": {
        _default: [
            { id: 'userName', label: "Teacher's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Ms. Johnson", type: 'text', templateLabel: "**Name:**" },
            { id: 'gradeLevel', label: "Grade Level", placeholder: "e.g., Elementary School", type: 'text', templateLabel: "**Grade Level:**" },
            { id: 'subjectArea', label: "Subject Area", placeholder: "e.g., Math, Science", type: 'text', templateLabel: "**Subject Area:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['encouraging', 'patient', 'informative', 'clear', 'engaging'].includes(s.value) || !commonCommunicationStyles.find(cs => cs.value === s.value)),
            },
            { id: 'relationshipGoal', label: 'Relationship Goal', placeholder: 'e.g., Supportive mentor', type: 'text' },
            { 
                id: 'proactivityLevel', 
                label: 'Proactivity Level', 
                type: 'dropdown',
                options: [
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High (Anticipates needs)', label: 'High (Anticipates needs)' },
                ]
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'studentSuccess', label: 'Student success' },
                    { value: 'fosteringCuriosity', label: 'Fostering curiosity' },
                    { value: 'inclusivity', label: 'Inclusivity' },
                    { value: 'lifelongLearning', label: 'Lifelong Learning' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Other Professionals": {
        "Real Estate Agent": [
            { id: 'userName', label: "Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Maria Rodriguez", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 2 years", type: 'text', templateLabel: "**Years of Experience:**" },
            { id: 'areasOfExpertise', label: "Area of Expertise", placeholder: "Residential Sales, Commercial Real Estate...", type: 'textarea', templateLabel: "**Area of Expertise:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['enthusiastic', 'friendly', 'informative', 'persuasive', 'negotiating'].includes(s.value) || !commonCommunicationStyles.find(cs => cs.value === s.value)),
            },
            { id: 'relationshipGoal', label: 'Relationship Goal', placeholder: 'e.g., Proactive agent', type: 'text' },
            { 
                id: 'proactivityLevel', 
                label: 'Proactivity Level', 
                type: 'dropdown',
                options: [
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High (Anticipates needs)', label: 'High (Anticipates needs)' },
                ]
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'clientSatisfaction', label: 'Client satisfaction' },
                    { value: 'transparency', label: 'Transparency' },
                    { value: 'marketKnowledge', label: 'Market Knowledge' },
                    { value: 'negotiationSkills', label: 'Negotiation Skills' },
                ],
                placeholder: "Add new key value"
            },
        ],
        _default: [
             { id: 'userName', label: "Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Alex Smith", type: 'text', templateLabel: "**Name:**" },
             {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles,
            },
            { id: 'relationshipGoal', label: 'Relationship Goal', placeholder: 'e.g., Helpful assistant', type: 'text' },
            { 
                id: 'proactivityLevel', 
                label: 'Proactivity Level', 
                type: 'dropdown',
                options: [
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High (Anticipates needs)', label: 'High (Anticipates needs)' },
                ]
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'customerService', label: 'Customer Service' },
                    { value: 'quality', label: 'Quality' },
                    { value: 'innovation', label: 'Innovation' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Faith Related Professionals": {
        "Islamic Scholar": []
    }
};



const CreatePrompts = forwardRef(({ chatbotId, isSetupMode, onFormValidityChange, onComplete, OnError }, ref) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    
    const [professions, setProfessions] = useState([]);
    const [professionOptions, setProfessionOptions] = useState([]);
    const [selectedProfession, setSelectedProfession] = useState(null);
    const [specialityOptions, setSpecialityOptions] = useState([]);
    const [selectedSpeciality, setSelectedSpeciality] = useState(null);
    const [subSkillOptions, setSubSkillOptions] = useState([]);
    const [selectedSubSkills, setSelectedSubSkills] = useState([]);
    const [newSubSkill, setNewSubSkill] = useState("");
    const [error, setError] = useState(null);
    const [assistantType, setAssistantType] = useState(null);

    const [currentDynamicFields, setCurrentDynamicFields] = useState([]);
    const [dynamicFieldValues, setDynamicFieldValues] = useState({});

    const [availableKeyValueOptions, setAvailableKeyValueOptions] = useState([]);
    const [newKeyValueInput, setNewKeyValueInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [companyName, setCompanyName] = useState("");
    const [userPhone, setUserPhone] = useState("");
    const [userEmail, setUserEmail] = useState("");

    // Custom styles for react-select to support dark mode
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: isDarkMode ? theme.palette.background.paper : '#fff',
            borderColor: isDarkMode ? theme.palette.divider : '#ccc',
            color: isDarkMode ? theme.palette.text.primary : '#000',
            '&:hover': {
                borderColor: isDarkMode ? theme.palette.primary.main : '#aaa',
            },
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? theme.palette.background.paper : '#fff',
            zIndex: 9999,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        menuList: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? theme.palette.background.paper : '#fff',
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
                width: '8px',
            },
            '&::-webkit-scrollbar-track': {
                background: isDarkMode ? theme.palette.background.default : '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
                background: isDarkMode ? theme.palette.divider : '#888',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
                background: isDarkMode ? theme.palette.action.hover : '#555',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused 
                ? (isDarkMode ? theme.palette.action.hover : '#f0f0f0')
                : (isDarkMode ? theme.palette.background.paper : '#fff'),
            color: isDarkMode ? theme.palette.text.primary : '#000',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: isDarkMode ? theme.palette.action.hover : '#f0f0f0',
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: isDarkMode ? theme.palette.text.primary : '#000',
        }),
        input: (provided) => ({
            ...provided,
            color: isDarkMode ? theme.palette.text.primary : '#000',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: isDarkMode ? theme.palette.text.secondary : '#999',
        }),
    };


    useEffect(() => {
        document.title = "Add Profession | KlonIT Web Application";
        fetchProfessions();

        if (chatbotId) {
            fetchChatbotDetails(chatbotId);
        }
    }, [chatbotId]);


    useEffect(() => {
        const fetchAndSetPromptSettings = async () => {
            if (!chatbotId || professions.length === 0 || !assistantType) return;
            
            setIsLoading(true);
            try {
                const response = await axiosInstance.get(`/prompt-settings/${chatbotId}`);
                
                if (response.data?.success) {
                    const { data } = response.data;
                    if (data.company_name) setCompanyName(data.company_name);
                    if (data.phone) setUserPhone(data.phone);
                    if (data.email) setUserEmail(data.email);

                    const profOption = data.profession ? { value: data.profession, label: data.profession } : null;
                    if (profOption) {
                        handleProfessionChange(profOption);
                        setTimeout(() => {
                            const specOption = data.speciality ? { value: data.speciality, label: data.speciality } : null;
                            if (specOption) {
                                handleSpecialityChange(specOption);
                                setTimeout(() => {
                                    if (Array.isArray(data.sub_skills)) {
                                        setSelectedSubSkills(data.sub_skills);
                                        setSubSkillOptions(prevOptions => {
                                            const existingValues = new Set(prevOptions.map(opt => opt.value));
                                            const newSkillsToAdd = data.sub_skills
                                                .filter(skill => !existingValues.has(skill))
                                                .map(skill => ({ value: skill, label: skill }));
                                            
                                            return [...prevOptions, ...newSkillsToAdd];
                                        });
                                    }
                                    if (typeof data.dynamic_fields === 'object' && data.dynamic_fields !== null) setDynamicFieldValues(data.dynamic_fields);
                                }, 150);
                            } else {
                                if (Array.isArray(data.sub_skills)) {
                                    setSelectedSubSkills(data.sub_skills);
                                    setSubSkillOptions(data.sub_skills.map(skill => ({ value: skill, label: skill })));
                                }
                            }
                        }, 150);
                    }
                }
            } catch (error) {
                if (error.response?.status !== 404) {
                    console.error("Error fetching prompt settings:", error);
                    setError("Could not load previously saved settings.");
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchAndSetPromptSettings();
    }, [chatbotId, professions, assistantType]);


    const fetchChatbotDetails = async (currentChatbotId) => {
        setIsLoading(true);
        try {
            let response ;
            let chatbotData = null;

            if (isSetupMode){
                response = await axiosInstance.get(`/chatbots/user/pending-process-bot`);
            
                if (response.status === 200) {
                    chatbotData = response.data.data.chatbot;
                }

            }else{
                response = await axiosInstance.get(
                    `/chatbots/${currentChatbotId}`
                );

                if (response.status === 200) {
                    chatbotData = response.data.data;
                }
            }

            
            if (chatbotData) {
                const assist_type = chatbotData.chatbot_type == 'business' ? 'Business Assistance' : 'Personal Assistant';
                setAssistantType(assist_type);
            }
        } catch (err) {
            console.error("Error fetching chatbot details:", err);
            setError("Error fetching chatbot details.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchProfessions = async () => {
        setIsLoading(true)
        try {
            const response = await axiosInstance.get(`/get-professions`);
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            if (responseData && responseData.length > 0) {
                setProfessions(responseData);
                const uniqueProfessions = [...new Map(responseData.map(item => [item.profession_name, item])).values()];
                setProfessionOptions(uniqueProfessions.map(item => ({ value: item.profession_name, label: item.profession_name })));
            } else {
                setError("Unexpected response structure or no professions found.");
            }
        } catch (err) {
            console.error("Error fetching professions:", err);
            setError("Error fetching professions.");
        } finally {
            setIsLoading(false)
        }
    };

    const updateUserPromptToLocalStorage = (data) => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const userData = JSON.parse(userStr);
            userData.llm_settings = data || null;
            localStorage.setItem('user', JSON.stringify(userData));
            window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData }));
            console.log("Local storage user subscription updated.");
        }
    };

    const handleProfessionChange = (selectedOption) => {
        setSelectedProfession(selectedOption);
        setSelectedSpeciality(null);
        setSelectedSubSkills([]);
        setCurrentDynamicFields([]);
        setDynamicFieldValues({});
        setAvailableKeyValueOptions([]);
        setNewKeyValueInput("");

        if (selectedOption && !selectedOption.__isNew__) {
            const filteredSpecialities = professions.filter(item => item.profession_name === selectedOption.value);
            const uniqueSpecialities = [...new Map(filteredSpecialities.map(item => [item.speciality, item])).values()];
            setSpecialityOptions(uniqueSpecialities.map(item => ({ value: item.speciality, label: item.speciality })));
        } else {
            setSpecialityOptions([]);
        }
        onFormValidityChange(false);
    };

    const handleSpecialityChange = (selectedOption) => {
    setSelectedSpeciality(selectedOption);
    setSelectedSubSkills([]);

    let config = [];
    if (selectedProfession && selectedOption) {
        const professionName = selectedProfession.value;
        const specialityName = selectedOption.value;

        if (dynamicFieldConfigurations[professionName]) {
            config = dynamicFieldConfigurations[professionName][specialityName] ||
                dynamicFieldConfigurations[professionName]._default ||
                [];
        } else if (selectedProfession && selectedProfession.__isNew__) {
            config = dynamicFieldConfigurations["Other Professionals"]?._default || [];
        }
        
        if (assistantType && assistantType !== 'Personal Assistant') {
            config = config.filter(field =>
                field.id !== 'relationshipGoal' && field.id !== 'proactivityLevel'
            );
        }

        setCurrentDynamicFields(config);

        const keyPrinciplesConfig = config.find(f => f.id === 'keyPrinciples' && f.type === 'checkboxGroup');

        if (keyPrinciplesConfig) {
            setAvailableKeyValueOptions(keyPrinciplesConfig.defaultOptions ? [...keyPrinciplesConfig.defaultOptions] : []);
        } else {
            setAvailableKeyValueOptions([]);
        }
        setNewKeyValueInput("");
        
        setDynamicFieldValues(prevValues => {
            const newValues = {};
            config.forEach(field => {
                if (prevValues[field.id] !== undefined) {
                    newValues[field.id] = prevValues[field.id];
                } else { 
                    if (field.id === 'keyPrinciples' && field.type === 'checkboxGroup') {
                        newValues[field.id] = [];
                    } else if (field.type === 'dropdown') {
                        newValues[field.id] = null;
                    } else {
                        newValues[field.id] = '';
                    }
                }
            });
            return newValues;
        });

        if (selectedOption && !selectedOption.__isNew__) {
            const filteredSubSkills = professions.filter(
                (item) => item.profession_name === professionName && item.speciality === specialityName
            );
            const subSkillOpts = [...new Map(filteredSubSkills.map((item) => [item.sub_skills, item])).values()].map((item) => ({
                value: item.sub_skills,
                label: item.sub_skills,
            }));
            setSubSkillOptions(subSkillOpts);
        } else {
            setSubSkillOptions([]);
        }

    } else {
        setCurrentDynamicFields([]);
        setDynamicFieldValues({});
        setAvailableKeyValueOptions([]);
        setNewKeyValueInput("");
        setSubSkillOptions([]);
    }
};

useEffect(() => {
    if (selectedProfession && selectedSpeciality && assistantType) {
        const timeoutId = setTimeout(() => {
            handleSpecialityChange(selectedSpeciality);
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }
}, [assistantType]);


    const handleDynamicFieldChange = (fieldId, value) => {
        setDynamicFieldValues(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleSubSkillCheck = (subSkill) => {
        setSelectedSubSkills((prev) =>
            prev.includes(subSkill) ? prev.filter((skill) => skill !== subSkill) : [...prev, subSkill]
        );
    };

    const handleAddSubSkill = () => {
        if (newSubSkill.trim()) {
            const newSkillValue = newSubSkill.trim();
            if (!subSkillOptions.some(opt => opt.value === newSkillValue)) {
                setSubSkillOptions((prev) => [...prev, { value: newSkillValue, label: newSkillValue }]);
            }
            if (!selectedSubSkills.includes(newSkillValue)) {
                setSelectedSubSkills((prev) => [...prev, newSkillValue]);
            }
            setNewSubSkill("");
        }
    };

    const handleKeyValueCheck = (value) => {
        setDynamicFieldValues(prev => {
            const currentSelected = prev.keyPrinciples || [];
            const newSelected = currentSelected.includes(value)
                ? currentSelected.filter(item => item !== value)
                : [...currentSelected, value];
            return { ...prev, keyPrinciples: newSelected };
        });
    };

    const handleAddKeyValue = () => {
        if (newKeyValueInput.trim()) {
            const newValue = newKeyValueInput.trim();
            const newOption = { value: newValue, label: newValue };

            if (!availableKeyValueOptions.some(opt => opt.value === newValue)) {
                setAvailableKeyValueOptions(prev => [...prev, newOption]);
            }

            setDynamicFieldValues(prev => {
                const currentSelected = prev.keyPrinciples || [];
                if (!currentSelected.includes(newValue)) {
                    return { ...prev, keyPrinciples: [...currentSelected, newValue] };
                }
                return prev;
            });
            setNewKeyValueInput("");
        }
    };


    const validateForm = () => {
        return selectedProfession && selectedSpeciality && selectedSubSkills.length > 0;
    };

    useEffect(() => {
        const isValid = validateForm();
        onFormValidityChange(isValid);
    }, [selectedProfession, selectedSpeciality, selectedSubSkills, dynamicFieldValues, onFormValidityChange]);


    const handleGeneratePrompt = async () => {
        if (!validateForm()) {
            setError("Please select a profession, speciality, and at least one sub-skill.");
            return false;
        }
        setError(null);
        setIsLoading(true);

        if (!chatbotId) {
            setError("Chatbot ID is missing. Cannot save settings or generate prompt.");
            setIsLoading(false);
            return false;
        }

        const settingsPayload = {
            chatbot_id: chatbotId,
            company_name: companyName.trim(),
            phone: userPhone.trim(),
            email: userEmail.trim(),
            profession: selectedProfession?.value || null,
            speciality: selectedSpeciality?.value || null,
            sub_skills: selectedSubSkills,
            dynamic_fields: dynamicFieldValues
        };

        try {
            await axiosInstance.post(`/prompt-settings`, settingsPayload);
            console.log("Prompt generation settings have been successfully saved.");
        } catch (saveError) {
            console.error("Error saving prompt settings:", saveError);
            setError(saveError.response?.data?.message || "Failed to save the form settings. The prompt was not generated.");
            setIsLoading(false);
            return false;
        }
        
        try {
            let prompt = "";
            const companyNameValue = settingsPayload.company_name || "[COMPANY_NAME_NOT_SPECIFIED]";
            const userPhoneValue = settingsPayload.phone || "[USER_PHONE_NOT_SPECIFIED]";
            const userEmailValue = settingsPayload.email || "[USER_EMAIL_NOT_SPECIFIED]";
            const UserProfession = selectedProfession?.label || "[UNKNOWN_PROFESSION]";
            const UserSpeciality = selectedSpeciality?.label || "[UNKNOWN_SPECIALITY]";
            const UserSubSkills = selectedSubSkills.join(", ").trim() || "[SUB_SKILLS_NOT_SPECIFIED]";

            if (assistantType === 'Personal Assistant') {
                const personalAssistantTemplate = `You are an expert virtual {Primary Industry/Field} personal assistant for [User's Name], specializing in {Key Topics within Field}. Your mission is to foster understanding, provide comprehensive guidance, engage conversationally, and facilitate user actions by proactively offering necessary links and steps.
Crucially: Use Current Date for reasoning internally; NEVER mention it explicitly.

Assistant Profile:
Name: [User Input Here]
Style: [Personality Style, e.g., "Friendly, Professional"], [Tone of Voice, e.g., "Warm, Informative"]
Goal: [Relationship Goal, e.g., "Trusted confidant"]
Proactivity: [Proactivity Level, e.g., "Highly proactive, anticipate needs"]
Principles: [Key Principles, e.g., "Accuracy, Confidentiality, Efficiency"]



Industry/Specialized Knowledge:
Primary Field: [User Input Here]
Key Topics: [User Input Here]
Info Delivery: [Information Delivery Preference, e.g., "Highlight critical changes immediately"]
Discussion: [Discussion Style, e.g., "Deep dives, Explaining complex concepts clearly"]



Data & RAG Priority:
Accesses a vector-based RAG knowledge base (user docs, drives, web crawls, emails, calendar).
PRIORITIZE RAG CONTEXT: Always use information from the provided RAG data over general knowledge for personalization.
Source Tags:
[External]: If augmenting solely with general knowledge.
[Hybrid]: If combining RAG + general knowledge.
(No tag): If solely from RAG.


Confidentiality: RAG data and interactions are strictly confidential.



Operational Directives & Interaction Protocol:
Information Synthesis:
User-Specific (RAG): Base answers on RAG for user-specific queries (meetings, projects, emails).
General Field Topics: Provide comprehensive answers, integrating RAG context and relevant links (e.g., FAQs, guides). Grounded in current industry standards/research from RAG.
Priority Actions (Mandatory Links/Steps):
For topics requiring user action (e.g., scheduling, document access, email replies, task initiation), ACTIVELY SEARCH RAG for the MOST RELEVANT LINK or specific action step.
Prioritize: Scheduling/direct access links (e.g., appointment portals, document links).
Proactive: Include even if not explicitly asked, if context indicates relevance.


Time-Sensitive: Use Current Date internally. Prioritize RAG-sourced deadlines/events. Clarify ambiguous requests.


Response Style:
Comprehensive & Actionable: Provide thorough, accessible answers. Include RAG context and MANDATORY priority links/actions (easy to find/use). Use warm, guiding tone.
Proactive Engagement: Offer deeper dives, invite further exploration, gently understand user intent, clarify missing details. Use conversation history.


Formatting:
Link Format: <a href="URL" target="_blank" style="color: blue;">[TOPIC]</a> # Output ONLY this raw HTML tag. Use specific topic for [TOPIC]. Ensure style="color: blue;" is exact. MANDATORY for Priority Actions & IMPORTANT RAG resources.
Email Management (RAG-driven): Draft context-aware replies for user review/send.
Calendar Management (RAG-driven): Update/schedule appointments based on user instructions and availability. Confirm changes.
Productivity (RAG-driven): Streamline workflows, summarize RAG documents/conversations, highlight deadlines/tasks, provide concise answers, deliver relevant news from RAG web crawls based on interests.



Scope & Refusal:
In Scope: RAG-answerable questions, in-depth field guidance with RAG context/links/actions, RAG-derived summaries/insights, email/calendar management.
Out of Scope: Personal advice beyond RAG context (medical, financial, legal), non-field/non-RAG topics, opinions/decisions without instruction, actions beyond RAG access (real-time external web search beyond crawled data, direct email sending).
Refusal Protocol (Helpful Tone):
RAG Missing: Explain missing info, suggest more details/document, offer general info ([External]).
Limits/Complexity: Explain limitations, suggest human review/expertise.
Out of Scope: State focus, offer in-scope alternatives. (e.g., "I'm unable to provide specific [out-of-scope] advice, but I can offer general information from your knowledge base related to [in-scope].")`;

                prompt = personalAssistantTemplate
                    .replace(/{Primary Industry\/Field}/g, UserSpeciality)
                    .replace(/\[User's Name\]/g, companyNameValue)
                    .replace(/{Key Topics within Field}/g, UserSubSkills)
                    .replace(/Primary Field: \[User Input Here\]/g, `Primary Field: ${UserSpeciality}`)
                    .replace(/Key Topics: \[User Input Here\]/g, `Key Topics: ${UserSubSkills}`);

                const assistantName = dynamicFieldValues.userName || `[Assistant's Name not specified]`;
                const principles = Array.isArray(dynamicFieldValues.keyPrinciples) && dynamicFieldValues.keyPrinciples.length > 0
                    ? dynamicFieldValues.keyPrinciples.join(', ')
                    : `[Key Principles not specified]`;
                const commStyle = dynamicFieldValues.communicationStyle || `[Communication Style not specified]`;
                const goal = dynamicFieldValues.relationshipGoal || `[Relationship Goal not specified]`;
                const proactivity = dynamicFieldValues.proactivityLevel || `[Proactivity Level not specified]`;
                
                prompt = prompt
                    .replace(/Name: \[User Input Here\]/, `Name: ${assistantName}`)
                    .replace(/Principles: \[Key Principles, e.g., "Accuracy, Confidentiality, Efficiency"\]/, `Principles: ${principles}`)
                    .replace(/Style: \[Personality Style, e.g., "Friendly, Professional"\], \[Tone of Voice, e.g., "Warm, Informative"\]/, `Style: ${commStyle}`)
                    .replace(/Goal: \[Relationship Goal, e.g., "Trusted confidant"\]/, `Goal: ${goal}`)
                    .replace(/Proactivity: \[Proactivity Level, e.g., "Highly proactive, anticipate needs"\]/, `Proactivity: ${proactivity}`);

                prompt = prompt
                    .replace(/Info Delivery: \[Information Delivery Preference, e.g., "Highlight critical changes immediately"\]/, "Info Delivery: [Information Delivery Preference not specified]")
                    .replace(/Discussion: \[Discussion Style, e.g., "Deep dives, Explaining complex concepts clearly"\]/, "Discussion: [Discussion Style not specified]");


            } else {
                const selectedEntry = professions.find(
                    (item) => item.profession_name === selectedProfession.value && item.speciality === selectedSpeciality.value
                );

                let rawPromptTemplate = selectedEntry?.prompt_template;

                if (!rawPromptTemplate) {
                    console.warn("No specific prompt template found. Using a generic fallback.");
                    rawPromptTemplate = `You are a virtual assistant for {companyName} in the field of {profession}, with expertise in {speciality} and {subskills}. Contact: {email}, {phone}.`;
                }

                prompt = String(rawPromptTemplate);

                prompt = prompt
                    .replace(/{speciality}/g, UserSpeciality)
                    .replace(/{companyName}/g, companyNameValue)
                    .replace(/{subskill1,subskil2,,}/g, UserSubSkills)
                    .replace(/{subskills}/g, UserSubSkills)
                    .replace(/{profession}/g, UserProfession)
                    .replace(/{email}/g, userEmailValue)
                    .replace(/{phone}/g, userPhoneValue);

                currentDynamicFields.forEach(field => {
                    let replacementValue;
                    const fieldValue = dynamicFieldValues[field.id];

                    if (field.type === 'checkboxGroup') {
                        replacementValue = (Array.isArray(fieldValue) ? fieldValue.join(", ") : '').trim();
                    } else if (field.type === 'dropdown') {
                        replacementValue = (fieldValue || '').trim();
                    } else {
                        replacementValue = (fieldValue || '').trim();
                    }

                    if (!replacementValue) {
                        replacementValue = `[${field.label.replace(/\s*\(Optional\)\s*|\s*\(Pick.*?\)\s*/i, "").trim()} not specified]`;
                    }

                    const escapedLabel = field.templateLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const regex = new RegExp(String.raw`(${escapedLabel}\s*)\[.*?\]`, 'g');

                    if (prompt.match(regex)) {
                        prompt = prompt.replace(regex, `$1${replacementValue}`);
                    } else {
                        const simplePlaceholderRegex = new RegExp(`{${field.id}}`, 'g');
                        if (prompt.match(simplePlaceholderRegex)) {
                            prompt = prompt.replace(simplePlaceholderRegex, replacementValue);
                        } else {
                            console.warn(`Template label "${field.templateLabel}" or placeholder "{${field.id}}" not found in prompt for field ID "${field.id}". Value: "${replacementValue}"`);
                        }
                    }
                });
            }
            
            const payload = { 
                prompt,
                chatbot_id: chatbotId
            };

            const response = await axiosInstance.post(`/update-prompt-setting`, payload);

            if (response.status === 200 || response.status === 201) {
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                setError(null);
                updateUserPromptToLocalStorage(responseData);
                onComplete(responseData);
                return true;
            } else {
                setError(response.data?.message || "Failed to update prompt.");
                return false;
            }
        } catch (err) {
            console.error("Error updating prompt:", err);
            setError("Error updating prompt. Please try again.");
            return false;
        } finally {
            setIsLoading(false)
        }
    };

    useImperativeHandle(ref, () => ({
        handleGeneratePrompt,
    }));

    const companyNameLabel = assistantType === 'Personal Assistant' ? 'Person Name' : 'Company Name';
    const companyNamePlaceholder = assistantType === 'Personal Assistant' ? 'Enter person name' : 'Enter company name';


    return (
        <Box  sx={{ position: 'relative' , backgroundColor: theme.palette.background.default,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.divider,
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: theme.palette.action?.hover || theme.palette.divider,
          },
        }}}>
            {isLoading && <Loader />}
            <Box sx={{ width: '100%', p: 0 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card sx={{ mb: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5" component="h4" color="textPrimary">
                                            Now let's capture details about your chatbot..
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                            {companyNameLabel}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={companyName}
                                            onChange={(e) => setCompanyName(e.target.value)}
                                            placeholder={companyNamePlaceholder}
                                            variant="outlined"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                            Profession Name
                                        </Typography>
                                        <CreatableSelect
                                            isClearable
                                            options={professionOptions}
                                            value={selectedProfession}
                                            onChange={handleProfessionChange}
                                            placeholder="Select or Add Profession"
                                            formatCreateLabel={userInput => `Add new profession: "${userInput}"`}
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                            Speciality
                                        </Typography>
                                        <CreatableSelect
                                            isClearable
                                            options={specialityOptions}
                                            value={selectedSpeciality}
                                            onChange={handleSpecialityChange}
                                            isDisabled={!selectedProfession}
                                            placeholder="Select or Add Speciality"
                                            formatCreateLabel={userInput => `Add new speciality: "${userInput}"`}
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </Grid>

                                    {selectedSpeciality && (
                                        <Grid item xs={12}>
                                            <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                                Sub Speciality
                                            </Typography>
                                            <Box sx={{ mb: 2 }}>
                                                {subSkillOptions.map((subSkill, index) => (
                                                    <Box key={`subskill-opt-${index}`} sx={{ display: 'block', mb: 0.5 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedSubSkills.includes(subSkill.value)}
                                                                    onChange={() => handleSubSkillCheck(subSkill.value)}
                                                                />
                                                            }
                                                            label={subSkill.label}
                                                        />
                                                    </Box>
                                                ))}
                                                <Box sx={{ display: 'flex', mt: 2, gap: 2 }}>
                                                    <TextField
                                                        fullWidth
                                                        value={newSubSkill}
                                                        onChange={(e) => setNewSubSkill(e.target.value)}
                                                        placeholder="Add new sub-skill"
                                                        variant="outlined"
                                                    />
                                                    <Tooltip title="Add new sub skill" arrow>
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleAddSubSkill}
                                                            sx={{ minWidth: '46px', height: '56px' }}
                                                        >
                                                            +
                                                        </Button>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    )}

                                    {currentDynamicFields.map(field => {
                                        if (field.type === 'dropdown') {
                                            return (
                                                <Grid item xs={12} key={field.id}>
                                                    <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                                        {field.label}
                                                    </Typography>
                                                    <CreatableSelect
                                                        isClearable
                                                        options={field.options}
                                                        value={field.options.find(opt => opt.value === dynamicFieldValues[field.id]) || null}
                                                        onChange={(selectedOption) => handleDynamicFieldChange(field.id, selectedOption ? selectedOption.value : null)}
                                                        placeholder={`Select ${field.label.split('(')[0].trim()}`}
                                                        styles={customSelectStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </Grid>
                                            );
                                        } else if (field.type === 'checkboxGroup' && field.id === 'keyPrinciples') {
                                            return (
                                                <Grid item xs={12} key={field.id}>
                                                    <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                                        {field.label}
                                                    </Typography>
                                                    <Box sx={{ mb: 2 }}>
                                                        {availableKeyValueOptions.map((option, index) => (
                                                            <Box key={`${field.id}-opt-${index}`} sx={{ display: 'block', mb: 0.5 }}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={(dynamicFieldValues[field.id] || []).includes(option.value)}
                                                                            onChange={() => handleKeyValueCheck(option.value)}
                                                                        />
                                                                    }
                                                                    label={option.label}
                                                                />
                                                            </Box>
                                                        ))}
                                                        <Box sx={{ display: 'flex', mt: 2, gap: 2 }}>
                                                            <TextField
                                                                fullWidth
                                                                value={newKeyValueInput}
                                                                onChange={(e) => setNewKeyValueInput(e.target.value)}
                                                                placeholder={field.placeholder || "Add new value"}
                                                                variant="outlined"
                                                            />
                                                            <Tooltip title={`Add new ${field.label.toLowerCase().split('(')[0].trim()}`} arrow>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={handleAddKeyValue}
                                                                    sx={{ minWidth: '46px', height: '56px' }}
                                                                >
                                                                    +
                                                                </Button>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            );
                                        } else if (field.type === 'textarea') {
                                            return (
                                                <Grid item xs={12} key={field.id}>
                                                    <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                                        {field.label}
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        value={dynamicFieldValues[field.id] || ''}
                                                        onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                                                        placeholder={field.placeholder}
                                                        variant="outlined"
                                                    />
                                                </Grid>
                                            );
                                        } else if (field.type === 'text') {
                                            return (
                                                <Grid item xs={12} key={field.id}>
                                                    <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                                        {field.label}
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        value={dynamicFieldValues[field.id] || ''}
                                                        onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                                                        placeholder={field.placeholder}
                                                        variant="outlined"
                                                    />
                                                </Grid>
                                            );
                                        }
                                        return null;
                                    })}
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5" component="h4" color="textPrimary">
                                            For questions requiring intervention, provide the following..
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                            Phone:
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={userPhone}
                                            onChange={(e) => setUserPhone(e.target.value)}
                                            placeholder="Enter phone number"
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" component="h5" sx={{ mb: 1 }} color="textPrimary">
                                            Email:
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={userEmail}
                                            onChange={(e) => setUserEmail(e.target.value)}
                                            placeholder="Enter email address"
                                            variant="outlined"
                                        />
                                    </Grid>
                                </Grid>
                                {error && (
                                    <Box sx={{ mt: 3 }}>
                                        <Alert severity="warning">{error}</Alert>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
});

export default CreatePrompts;